import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Building2,
  Users,
  CheckCircle,
  PauseCircle,
  ChevronRight,
  LogIn,
  Server,
  ShieldCheck,
  Globe,
  Activity,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { StatCard, Card } from '../components/ui/Card.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  schemaName: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtRelative(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmt(d);
}

const PLAN_MAP: Record<string, 'purple' | 'info' | 'success' | 'default'> = {
  enterprise: 'purple',
  pro: 'info',
  standard: 'success',
  free: 'default',
};

const STATUS_MAP: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  deleted: 'danger',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminOverviewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [enteringId, setEnteringId] = useState<string | null>(null);

  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['admin-tenants'],
    queryFn: () => api.get('/sa/tenants').then((r) => r.data),
  });

  const impersonateMutation = useMutation({
    mutationFn: (tenantId: string) =>
      api
        .post<{
          token: string;
          tenantId: string;
          tenantName: string;
          tenantSlug: string;
        }>(`/sa/tenants/${tenantId}/impersonate`, {})
        .then((r) => r.data),
    onSuccess: (data) => {
      const base = import.meta.env.VITE_TENANT_BASE_DOMAIN ?? '';
      window.location.href = `https://${data.tenantSlug}.${base}?token=${encodeURIComponent(data.token)}`;
    },
    onError: (err) => {
      setEnteringId(null);
      toast.error(extractError(err));
    },
  });

  function handleEnter(id: string) {
    setEnteringId(id);
    impersonateMutation.mutate(id);
  }

  // ── Computed stats ───────────────────────────────────────────────────────────
  const total = tenants.length;
  const active = tenants.filter((t) => t.status === 'active').length;
  const suspended = tenants.filter((t) => t.status === 'suspended').length;
  const sorted = [...tenants].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="space-y-8">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <PageHeader
        title="Platform Overview"
        description="Super-admin control panel — manage tenants, users and platform settings"
      />

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total tenants"
          value={isLoading ? '—' : total}
          icon={<Building2 size={18} />}
        />
        <StatCard
          label="Active tenants"
          value={isLoading ? '—' : active}
          sub={total > 0 ? `${Math.round((active / total) * 100)}% of total` : undefined}
          icon={<CheckCircle size={18} />}
          trend={active > 0 ? { value: active, label: 'active' } : undefined}
        />
        <StatCard
          label="Suspended"
          value={isLoading ? '—' : suspended}
          icon={<PauseCircle size={18} />}
        />
        <StatCard
          label="Schemas"
          value={isLoading ? '—' : total}
          sub="dedicated PG schemas"
          icon={<Server size={18} />}
        />
      </div>

      {/* ── Main grid ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant list — spans 2 cols */}
        <div className="lg:col-span-2">
          <Card padding="none">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Building2 size={15} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">Tenants</h2>
                {!isLoading && <span className="text-xs text-zinc-400">· {total}</span>}
              </div>
              <Button size="sm" onClick={() => navigate('/admin/tenants')}>
                Manage all
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-50">
                    <th className="px-5 pb-2 pt-3 text-left text-xs font-medium text-zinc-400">
                      Tenant
                    </th>
                    <th className="px-5 pb-2 pt-3 text-left text-xs font-medium text-zinc-400">
                      Plan
                    </th>
                    <th className="px-5 pb-2 pt-3 text-left text-xs font-medium text-zinc-400">
                      Status
                    </th>
                    <th className="px-5 pb-2 pt-3 text-left text-xs font-medium text-zinc-400">
                      Created
                    </th>
                    <th className="px-5 pb-2 pt-3 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {isLoading ? (
                    <SkeletonRows count={4} cols={5} />
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-sm text-zinc-400">
                        No tenants yet. Create one from the Tenants page.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((t) => (
                      <tr
                        key={t.id}
                        className="hover:bg-zinc-50/60 transition-colors group cursor-pointer"
                        onClick={() => navigate(`/sa/tenants/${t.id}`)}
                      >
                        <td className="px-5 py-3">
                          <p className="font-medium text-zinc-900">{t.name}</p>
                          <code className="text-[11px] text-zinc-400 font-mono">{t.slug}</code>
                        </td>
                        <td className="px-5 py-3">
                          <Badge variant={PLAN_MAP[t.plan] ?? 'default'}>{t.plan}</Badge>
                        </td>
                        <td className="px-5 py-3">
                          <Badge variant={STATUS_MAP[t.status] ?? 'default'} dot>
                            {t.status}
                          </Badge>
                        </td>
                        <td className="px-5 py-3 text-xs text-zinc-400 whitespace-nowrap">
                          {fmtRelative(t.createdAt)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {t.status === 'active' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEnter(t.id);
                                }}
                                disabled={enteringId === t.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors disabled:opacity-50"
                                title={`Enter ${t.name}`}
                              >
                                {enteringId === t.id ? (
                                  <span className="animate-pulse">…</span>
                                ) : (
                                  <>
                                    <LogIn size={11} />
                                    Enter
                                  </>
                                )}
                              </button>
                            )}
                            <ChevronRight size={13} className="text-zinc-300" />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Platform info */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={15} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Platform</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Environment</span>
                <Badge variant={import.meta.env.PROD ? 'success' : 'warning'}>
                  {import.meta.env.PROD ? 'production' : 'staging'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Version</span>
                <code className="text-xs font-mono text-zinc-600">v0.1.0</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Auth</span>
                <Badge variant="info">JWT · 8h</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">DB</span>
                <Badge variant="success">Aurora Postgres</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Runtime</span>
                <Badge variant="default">Lambda · eu-west-1</Badge>
              </div>
            </div>
          </Card>

          {/* Quick links */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Activity size={15} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Quick actions</h2>
            </div>
            <div className="space-y-1">
              {[
                {
                  icon: Building2,
                  label: 'Manage tenants',
                  sub: 'Create, edit, suspend',
                  to: '/admin/tenants',
                },
                {
                  icon: Users,
                  label: 'Tenant users',
                  sub: 'Add users to a tenant',
                  to: '/admin/tenants',
                },
                {
                  icon: Globe,
                  label: 'Enter a tenant',
                  sub: 'Impersonate & debug',
                  to: '/admin/tenants',
                },
              ].map(({ icon: Icon, label, sub, to }) => (
                <button
                  key={label}
                  onClick={() => navigate(to)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-zinc-50 transition-colors text-left group"
                >
                  <div className="p-1.5 bg-zinc-100 rounded-md group-hover:bg-zinc-200 transition-colors flex-shrink-0">
                    <Icon size={13} className="text-zinc-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800">{label}</p>
                    <p className="text-xs text-zinc-400">{sub}</p>
                  </div>
                  <ChevronRight size={13} className="text-zinc-300 ml-auto flex-shrink-0" />
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
